/**
 * HTML tag overrides for react-markdown.
 * Handles standard HTML elements (a, div, ul, ol, etc.) with custom rendering
 * logic for legacy Strapi block types and content formatting.
 */
import { toHtml } from "hast-util-to-html";
import cn from "classnames";
import { omit } from "lodash";

import { Link } from "../components/Link";
import { SITE_DOMAIN } from "@/shared/config/constants";

import { removeTableStyles, formatOl } from "@/components/pages/blog/ai-article/helpers/content";

const isInternalHashLink = (href?: string | null): boolean => {
  if (!href) return false;

  if (href.startsWith("#")) return true;

  try {
    const url = new URL(href, window.location.href);

    return (
      url.origin === window.location.origin &&
      url.pathname === window.location.pathname &&
      Boolean(url.hash) &&
      !url.search
    );
  } catch {
    return false;
  }
};

export const htmlOverrides = {
  a: ({ node, className, ...props }) => {
    const isExternalLink =
      (!props?.href?.match(new RegExp(SITE_DOMAIN, "img")) || "blank" in props) &&
      !props?.href?.startsWith("/");

    const href = props?.href;

    const isHashLink = isInternalHashLink(href);

    return (
      <Link
        className={cn(className, "link")}
        target={isHashLink ? "_self" : isExternalLink ? "_blank" : "_self"}
        {...props}
        href={href}
      />
    );
  },

  pre: ({ node, ...props }) => {
    if (node.children.length === 1 && node.children[0].tagName === "code") {
      return <div dangerouslySetInnerHTML={{ __html: node.children[0].children[0].value }} />;
    }

    return <pre {...props} />;
  },
  table: ({ node, ...props }) => {
    return <div className="table-wrap" dangerouslySetInnerHTML={{ __html: removeTableStyles(node) }} />;
  },
  img: ({ node, ...props }) => {
    return <img {...props} loading="lazy" decoding="async" />;
  },
  ol: ({ node, ...props }) => (
    <ol {...omit(props, "children")} dangerouslySetInnerHTML={{ __html: formatOl(toHtml(node.children)) }} />
  ),
};
